import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import PrivateRoute from './components/PrivateRoute';
import SystemAdminRoute from './components/SystemAdminRoute';
import TenantRoute from './components/TenantRoute';
import HomeRedirect from './components/HomeRedirect';
import KioskRoute from './components/KioskRoute';
import {
  WORKERS_IMPORT_CONFIG,
  WORK_ORDERS_IMPORT_CONFIG,
  WORK_ORDER_OPERATIONS_IMPORT_CONFIG,
  WORK_GROUPS_IMPORT_CONFIG,
  INVENTORY_IMPORT_CONFIG,
  ATTENDANCE_IMPORT_CONFIG,
  WORK_LOG_IMPORT_CONFIG,
  QUALITY_INSPECTION_IMPORT_CONFIG,
  POSITIONS_IMPORT_CONFIG,
} from './config/importConfigs';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const WorkOrders = lazy(() => import('./pages/WorkOrders'));
const WorkOrdersV1 = lazy(() => import('./pages/style-lab/WorkOrdersV1'));
const WorkOrdersV2 = lazy(() => import('./pages/style-lab/WorkOrdersV2'));
const WorkOrdersV3 = lazy(() => import('./pages/style-lab/WorkOrdersV3'));
const Operations = lazy(() => import('./pages/Operations'));
const WorkCenters = lazy(() => import('./pages/WorkCenters'));
const Workers = lazy(() => import('./pages/Workers'));
const WorkLog = lazy(() => import('./pages/WorkLog'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Attendance = lazy(() => import('./pages/Attendance'));
const OvertimeApprovals = lazy(() => import('./pages/OvertimeApprovals'));
const Payroll = lazy(() => import('./pages/Payroll'));
const AttendanceValidateRedirect = lazy(() => import('./pages/AttendanceValidateRedirect'));
const ClockSessionIssues = lazy(() => import('./pages/ClockSessionIssues'));
const BulkAttendanceEntry = lazy(() => import('./pages/BulkAttendanceEntry'));
const Reports = lazy(() => import('./pages/Reports'));
const CustomReports = lazy(() => import('./pages/CustomReports'));
const CustomReportsLibrary = lazy(() => import('./pages/CustomReportsLibrary'));
const Queries = lazy(() => import('./pages/Queries'));
const QueriesLibrary = lazy(() => import('./pages/QueriesLibrary'));
const ManualEntries = lazy(() => import('./pages/ManualEntries'));
const Users = lazy(() => import('./pages/Users'));
const ActivityLogs = lazy(() => import('./pages/ActivityLogs'));
const TenantSettings = lazy(() => import('./pages/TenantSettings'));
const Billing = lazy(() => import('./pages/Billing'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminTenants = lazy(() => import('./pages/admin/Tenants'));
const AdminSystemAdmins = lazy(() => import('./pages/admin/SystemAdmins'));
const AdminActivityLogs = lazy(() => import('./pages/admin/ActivityLogs'));
const TenantUsers = lazy(() => import('./pages/admin/TenantUsers'));
const ScanTickets = lazy(() => import('./pages/ScanTickets'));
const QrScan = lazy(() => import('./pages/QrScan'));
const KioskActivate = lazy(() => import('./pages/kiosk/KioskActivate'));
const KioskHome = lazy(() => import('./pages/kiosk/KioskHome'));
const KioskAttendance = lazy(() => import('./pages/kiosk/KioskAttendance'));
const KioskScan = lazy(() => import('./pages/kiosk/KioskScan'));
const Shifts = lazy(() => import('./pages/Shifts'));
const WorkerProfile = lazy(() => import('./pages/WorkerProfile'));
const QualityInspection = lazy(() => import('./pages/QualityInspection'));
const WorkOrderDetail = lazy(() => import('./pages/WorkOrderDetail'));
const Positions = lazy(() => import('./pages/Positions'));
const PayrollRules = lazy(() => import('./pages/PayrollRules'));
const SkillGroups = lazy(() => import('./pages/SkillGroups'));
const CertificationSuggestions = lazy(() => import('./pages/CertificationSuggestions'));
const Home = lazy(() => import('./pages/Home'));
const OtReviewRedirect = lazy(() => import('./pages/OtReviewRedirect'));
const WorkCenterDetail = lazy(() => import('./pages/WorkCenterDetail'));
const WorkCenterCosting = lazy(() => import('./pages/WorkCenterCosting'));
const WorkCenterFlow = lazy(() => import('./pages/WorkCenterFlow'));
const OperationDetail = lazy(() => import('./pages/OperationDetail'));
const WorkGroups = lazy(() => import('./pages/WorkGroups'));
const ReportTemplates = lazy(() => import('./pages/ReportTemplates'));
const ReportTemplateEditor = lazy(() => import('./pages/ReportTemplateEditor'));
const RolesPermissions = lazy(() => import('./pages/RolesPermissions'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const McpConnector = lazy(() => import('./pages/McpConnector'));
const McpAuthorize = lazy(() => import('./pages/McpAuthorize'));
const Extensions = lazy(() => import('./pages/Extensions'));
const ApprovalRoutes = lazy(() => import('./pages/ApprovalRoutes'));
const AutomationRules = lazy(() => import('./pages/AutomationRules'));
const AutomationRuleForm = lazy(() => import('./pages/AutomationRuleForm'));
const Approvals = lazy(() => import('./pages/Approvals'));
const WorkGroupDetail = lazy(() => import('./pages/WorkGroupDetail'));
const WorkerForm = lazy(() => import('./pages/WorkerForm'));
const WorkOrderForm = lazy(() => import('./pages/WorkOrderForm'));
const InventoryForm = lazy(() => import('./pages/InventoryForm'));
const AttendanceForm = lazy(() => import('./pages/AttendanceForm'));
const WorkLogForm = lazy(() => import('./pages/WorkLogForm'));
const WorkLogBulkGridEntry = lazy(() => import('./pages/WorkLogBulkGridEntry'));
const QualityInspectionForm = lazy(() => import('./pages/QualityInspectionForm'));
const UserForm = lazy(() => import('./pages/UserForm'));
const CsvImportPage = lazy(() => import('./pages/CsvImportPage'));

const theme = createTheme({
  // MUI defaults (xs:0, sm:600, md:900, lg:1200, xl:1536) leave the whole
  // 900-1200px band as plain `md`, even though real 13" laptops render at
  // ~1280px effective width (or less under OS display scaling) — just above
  // the lg cliff Layout.js uses for mega-menu vs. hamburger. Components that
  // want a deliberate "compact desktop" density between tablet and full
  // desktop should branch on `laptop` (975px) rather than only ever using
  // `md`/`lg`, since `md` alone still spans phone-adjacent tablet widths too.
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, laptop: 975, lg: 1200, xl: 1536 },
  },
  palette: {
    primary: {
      main: '#5c6bc0',
      dark: '#3949ab',
      light: '#7986cb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#26a69a',
      dark: '#00796b',
      contrastText: '#ffffff',
    },
    success: {
      main: '#28a745',
      light: '#d4edda',
    },
    warning: {
      main: '#fd7e14',
      light: '#fff3cd',
    },
    error: {
      main: '#dc3545',
    },
    info: {
      main: '#17a2b8',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212529',
      secondary: '#6c757d',
    },
    divider: '#e9ecef',
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', sans-serif",
    h1: { fontSize: '1.75rem', fontWeight: 600 },
    h2: { fontSize: '1.5rem', fontWeight: 600 },
    h3: { fontSize: '1.25rem', fontWeight: 600 },
    h4: { fontSize: '1.125rem', fontWeight: 600 },
    h5: { fontSize: '1rem', fontWeight: 600 },
    h6: { fontSize: '0.875rem', fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem' },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)',
    '0 10px 15px rgba(0,0,0,0.07), 0 4px 6px rgba(0,0,0,0.05)',
    '0 20px 25px rgba(0,0,0,0.07), 0 10px 10px rgba(0,0,0,0.04)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.12)',
  ],
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          padding: '6px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.15)' },
        },
        sizeSmall: { padding: '4px 10px', fontSize: '0.75rem' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #e9ecef',
          borderRadius: 10,
          boxShadow: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '20px 24px', '&:last-child': { paddingBottom: '20px' } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8f9fa',
          color: '#6c757d',
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          padding: '10px 16px',
          borderBottom: '1px solid #dee2e6',
        },
        body: {
          fontSize: '0.8125rem',
          padding: '10px 16px',
          borderBottom: '1px solid #f1f3f5',
          color: '#212529',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#f8f9fa' },
          '&.Mui-selected': { backgroundColor: '#e8eaf6' },
          '&.Mui-selected:hover': { backgroundColor: '#dde0f5' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        colorSuccess: { backgroundColor: '#d4edda', color: '#155724' },
        colorWarning: { backgroundColor: '#fff3cd', color: '#856404' },
        colorError: { backgroundColor: '#f8d7da', color: '#721c24' },
        colorInfo: { backgroundColor: '#d1ecf1', color: '#0c5460' },
        colorPrimary: { backgroundColor: '#e8eaf6', color: '#3949ab' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#212529',
          borderBottom: '1px solid #e9ecef',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a237e',
          color: '#c5cae9',
          borderRight: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          padding: '20px 24px 12px',
          borderBottom: '1px solid #e9ecef',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#dee2e6',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#adb5bd',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 44,
          padding: '6px 16px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '1px 8px',
          padding: '7px 12px',
          color: '#495057',
          '&:hover': {
            backgroundColor: '#f1f2fb',
            color: '#495057',
          },
          '&.Mui-selected': {
            backgroundColor: '#e8eaf6',
            color: '#3949ab',
            '&:hover': { backgroundColor: '#dde0f5' },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
          minWidth: 36,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: { borderRadius: 6, fontSize: '0.8125rem' },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      },
    },
  },
});

const routeSweep = keyframes`
  0% { left: -40%; width: 30%; }
  55% { left: 100%; width: 55%; }
  100% { left: 100%; width: 55%; }
`;

// Slim gradient sweep — shown while a lazy-loaded route chunk downloads.
// Replaces the old indeterminate LinearProgress with an eased, two-tone bar.
function RouteLoadingFallback() {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        overflow: 'hidden',
        bgcolor: 'transparent',
        zIndex: 2000,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          height: '100%',
          borderRadius: '0 4px 4px 0',
          background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          animation: `${routeSweep} 1.4s cubic-bezier(.4,0,.2,1) infinite`,
        }}
      />
    </Box>
  );
}

// Shared query client for cross-page caching/deduping of read-mostly data
// (dropdown/picker lists: workers, work centers, operations, work groups,
// etc.) — replaces the previous pattern of every page independently
// fetching "all X" fresh on every mount. staleTime is intentionally
// moderate (2 min): this data changes rarely, but should still pick up
// edits made elsewhere within a normal working session.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
      <AuthProvider>
        <Router>
          <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* OAuth consent for the MCP Connector. Public route: it shows its own sign-in. */}
            <Route path="/mcp/authorize" element={<McpAuthorize />} />

            {/* System Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <SystemAdminRoute>
                  <AdminDashboard />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/admin/tenants"
              element={
                <SystemAdminRoute>
                  <AdminTenants />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/admin/tenants/:tenantId/users"
              element={
                <SystemAdminRoute>
                  <TenantUsers />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/admin/system-admins"
              element={
                <SystemAdminRoute>
                  <AdminSystemAdmins />
                </SystemAdminRoute>
              }
            />
            <Route
              path="/admin/activity-logs"
              element={
                <SystemAdminRoute>
                  <AdminActivityLogs />
                </SystemAdminRoute>
              }
            />
            
            {/* Tenant Routes - Path-based routing */}
            <Route
              path="/app/:tenantCode/home"
              element={
                <TenantRoute>
                  <Home />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/dashboard"
              element={
                <TenantRoute>
                  <Dashboard />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-orders/new"
              element={
                <TenantRoute>
                  <WorkOrderForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-orders/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={WORK_ORDERS_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-orders/import-operations"
              element={
                <TenantRoute>
                  <CsvImportPage config={WORK_ORDER_OPERATIONS_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-orders/:workOrderId"
              element={
                <TenantRoute>
                  <WorkOrderDetail />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-orders"
              element={
                <TenantRoute>
                  <WorkOrders />
                </TenantRoute>
              }
            />
            {process.env.NODE_ENV !== 'production' && (
              <>
                <Route
                  path="/app/:tenantCode/style-lab/work-orders-v1"
                  element={
                    <TenantRoute>
                      <WorkOrdersV1 />
                    </TenantRoute>
                  }
                />
                <Route
                  path="/app/:tenantCode/style-lab/work-orders-v2"
                  element={
                    <TenantRoute>
                      <WorkOrdersV2 />
                    </TenantRoute>
                  }
                />
                <Route
                  path="/app/:tenantCode/style-lab/work-orders-v3"
                  element={
                    <TenantRoute>
                      <WorkOrdersV3 />
                    </TenantRoute>
                  }
                />
              </>
            )}
            <Route
              path="/app/:tenantCode/work-groups/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={WORK_GROUPS_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-groups/:workGroupId"
              element={
                <TenantRoute>
                  <WorkGroupDetail />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-groups"
              element={
                <TenantRoute>
                  <WorkGroups />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/operations/:operationId"
              element={
                <TenantRoute>
                  <OperationDetail />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/operations"
              element={
                <TenantRoute>
                  <Operations />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-centers/costing"
              element={
                <TenantRoute>
                  <WorkCenterCosting />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-centers/flow"
              element={
                <TenantRoute>
                  <WorkCenterFlow />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-centers/new"
              element={
                <TenantRoute>
                  <WorkCenterDetail />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-centers/:workCenterId"
              element={
                <TenantRoute>
                  <WorkCenterDetail />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-centers"
              element={
                <TenantRoute>
                  <WorkCenters />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/workers/new"
              element={
                <TenantRoute>
                  <WorkerForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/workers/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={WORKERS_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/workers/:workerId"
              element={
                <TenantRoute>
                  <WorkerProfile />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/workers"
              element={
                <TenantRoute>
                  <Workers />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/positions/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={POSITIONS_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/positions"
              element={
                <TenantRoute>
                  <Positions />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/payroll-rules"
              element={
                <TenantRoute>
                  <PayrollRules />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-log/new"
              element={
                <TenantRoute>
                  <WorkLogForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-log/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={WORK_LOG_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-log/bulk-grid-entry"
              element={
                <TenantRoute>
                  <WorkLogBulkGridEntry />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-log/:logId/edit"
              element={
                <TenantRoute>
                  <WorkLogForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/work-log"
              element={
                <TenantRoute>
                  <WorkLog />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/inventory/new"
              element={
                <TenantRoute>
                  <InventoryForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/inventory/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={INVENTORY_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/inventory/:movementId/edit"
              element={
                <TenantRoute>
                  <InventoryForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/inventory"
              element={
                <TenantRoute>
                  <Inventory />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/new"
              element={
                <TenantRoute>
                  <AttendanceForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={ATTENDANCE_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/:attendanceId/edit"
              element={
                <TenantRoute>
                  <AttendanceForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance"
              element={
                <TenantRoute>
                  <Attendance />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/overtime-approvals"
              element={
                <TenantRoute>
                  <OvertimeApprovals />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/payroll"
              element={
                <TenantRoute>
                  <Payroll />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/validate"
              element={
                <TenantRoute>
                  <AttendanceValidateRedirect />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/clock-session-issues"
              element={
                <TenantRoute>
                  <ClockSessionIssues />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/bulk-entry"
              element={
                <TenantRoute>
                  <BulkAttendanceEntry />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/attendance/ot-review/:date"
              element={
                <TenantRoute>
                  <OtReviewRedirect />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/reports"
              element={
                <TenantRoute>
                  <Reports />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/reports/:reportSlug"
              element={
                <TenantRoute>
                  <Reports />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/custom-reports"
              element={
                <TenantRoute>
                  <CustomReportsLibrary />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/custom-reports/new"
              element={
                <TenantRoute>
                  <CustomReports />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/custom-reports/:reportId"
              element={
                <TenantRoute>
                  <CustomReports />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/queries"
              element={
                <TenantRoute>
                  <QueriesLibrary />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/queries/new"
              element={
                <TenantRoute>
                  <Queries />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/queries/:queryId"
              element={
                <TenantRoute>
                  <Queries />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/manual-entries"
              element={
                <TenantRoute>
                  <ManualEntries />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/users/new"
              element={
                <TenantRoute>
                  <UserForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/users/:userId/edit"
              element={
                <TenantRoute>
                  <UserForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/users"
              element={
                <TenantRoute>
                  <Users />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/activity-logs"
              element={
                <TenantRoute>
                  <ActivityLogs />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/tenant-settings"
              element={
                <TenantRoute>
                  <TenantSettings />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/billing"
              element={
                <TenantRoute>
                  <Billing />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/scan-tickets"
              element={
                <TenantRoute>
                  <ScanTickets />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/scan"
              element={
                <TenantRoute>
                  <QrScan />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/kiosk/activate"
              element={
                <TenantRoute>
                  <KioskActivate />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/kiosk/attendance"
              element={
                <KioskRoute>
                  <KioskAttendance />
                </KioskRoute>
              }
            />
            <Route
              path="/app/:tenantCode/kiosk/scan"
              element={
                <KioskRoute>
                  <KioskScan />
                </KioskRoute>
              }
            />
            <Route
              path="/app/:tenantCode/kiosk"
              element={
                <KioskRoute>
                  <KioskHome />
                </KioskRoute>
              }
            />
            <Route
              path="/app/:tenantCode/skill-groups"
              element={
                <TenantRoute>
                  <SkillGroups />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/certification-suggestions"
              element={
                <TenantRoute>
                  <CertificationSuggestions />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/shifts"
              element={
                <TenantRoute>
                  <Shifts />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/report-templates/new"
              element={
                <TenantRoute>
                  <ReportTemplateEditor />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/report-templates/:templateId/edit"
              element={
                <TenantRoute>
                  <ReportTemplateEditor />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/report-templates"
              element={
                <TenantRoute>
                  <ReportTemplates />
                </TenantRoute>
              }
            />
<Route
              path="/app/:tenantCode/quality/new"
              element={
                <TenantRoute>
                  <QualityInspectionForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/quality/import"
              element={
                <TenantRoute>
                  <CsvImportPage config={QUALITY_INSPECTION_IMPORT_CONFIG} />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/quality/:inspectionId/edit"
              element={
                <TenantRoute>
                  <QualityInspectionForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/quality"
              element={
                <TenantRoute>
                  <QualityInspection />
                </TenantRoute>
              }
            />

            <Route
              path="/app/:tenantCode/approvals"
              element={
                <TenantRoute>
                  <Approvals />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/roles"
              element={
                <TenantRoute>
                  <RolesPermissions />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/api-keys"
              element={
                <TenantRoute>
                  <ApiKeys />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/mcp-connector"
              element={
                <TenantRoute>
                  <McpConnector />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/extensions"
              element={
                <TenantRoute>
                  <Extensions />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/approval-routes"
              element={
                <TenantRoute>
                  <ApprovalRoutes />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/automation-rules"
              element={
                <TenantRoute>
                  <AutomationRules />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/automation-rules/new"
              element={
                <TenantRoute>
                  <AutomationRuleForm />
                </TenantRoute>
              }
            />
            <Route
              path="/app/:tenantCode/settings/automation-rules/:ruleId/edit"
              element={
                <TenantRoute>
                  <AutomationRuleForm />
                </TenantRoute>
              }
            />

            {/* Root route - redirect based on user type */}
            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;


